import { find, isEmpty } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useMemoizedFn } from 'ahooks';
import { DialogPlugin } from 'tdesign-react';
import { sleep } from '@/utils/common';
import InfoItem from './InfoItem';

// 获取当前选中的集群
export function getCurrentCluster(clusterList) {
  if (isEmpty(clusterList)) return null;
  return find(clusterList, { selected: true });
}

// 判断当前集群是否是kafka
export function isKafkaCluster(clusterList) {
  return getCurrentCluster(clusterList)?.clusterType === 'kafka';
}

// 集群选择插件
export default function CulsterAddon({
  collapsed,
  fetchAllClusterList,
  allList,
  selectCuster,
  currentCluster,
  clusterType,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const clusterName = searchParams.get('name');

  const singleCluster = useMemo(() => {
    return (allList || []).length <= 1;
  }, [allList]);

  const loadClusters = async () => {
    const { payload: clustersArray = [] } = await fetchAllClusterList(clusterName);
    if (isEmpty(clustersArray)) {
      const dialog = DialogPlugin.alert({
        theme: 'warning',
        body: '暂无集群，请先部署',
        header: '提示',
        onConfirm: () => dialog.hide(),
        onClose: () => dialog.hide(),
      });
      return;
    }
    // 如果url中没有选中的集群，同时立即修正
    if (!clusterName) {
      setSearchParams({ name: clustersArray.find((item) => item.selected).clusterName });
      return;
    }
  };

  const doInit = useMemoizedFn(async () => {
    // 已加载过，内存中有，但url中没有，则修正
    if (currentCluster) {
      if (!clusterName) {
        await sleep(100);
        setSearchParams({ name: currentCluster.clusterName });
        return;
      }
      // 发生变化
      if (currentCluster.clusterName !== clusterName) {
        selectCuster(clusterName);
        return;
      }
    }
    // 还没有加载数据， 先拉数据
    if (isEmpty(allList)) {
      loadClusters();
    }
  });

  useEffect(() => {
    doInit(); // 加载所有集群, 并选择默认值（或者第一个）
  }, [doInit, clusterName]);

  const selectCluster = (name, selectCluster) => {
    selectCuster(name);
    // 集群类型不同
    if (clusterType === 'kop' && currentCluster?.clusterType !== selectCluster?.clusterType) {
      navigate(`/kop-cluster/dashboard?name=${name}`);
      return;
    }
    setSearchParams({ name });
  };

  return (
    <Root>
      <Container $hoverable={!singleCluster} collapsed={collapsed}>
        <Row>
          <InfoItem title={currentCluster?.displayName} subTitle={currentCluster?.clusterName} />
          {!singleCluster && <Icon src={require('./images/change.png')} />}
        </Row>

        <MenuContainer className='body narrow-scrollbar'>
          {allList.map((item) => (
            <MenuItem
              key={item.clusterName}
              onClick={() => selectCluster(item.clusterName, item)}
              selected={item.selected}
            >
              <InfoItem title={item.displayName} subTitle={item.clusterName} />
            </MenuItem>
          ))}
        </MenuContainer>
      </Container>
    </Root>
  );
}

const Root = styled.div`
  margin: 16px 8px 0 10px;
  height: 40px;
  position: relative;
`;

const Container = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 40px;
  z-index: 100;
  box-sizing: border-box;
  border: 1px solid rgba(13, 17, 26, 0.1);
  border-radius: 6px;
  transition: ${(props) =>
    props.collapsed
      ? 'width 0.2s cubic-bezier(0.215, 0.61, 0.355, 1), height 0.3s cubic-bezier(0.215, 0.61, 0.355, 1)'
      : 'all 0.3s cubic-bezier(0.215, 0.61, 0.355, 1) '};
  width: ${(props) => (props.collapsed ? 46 : 182)}px;
  .info-block {
    display: ${(props) => (props.collapsed ? 'none' : 'block')};
  }
  .body {
    height: 0;
    border-top-width: 0;
  }
  ${(props) =>
    props.$hoverable &&
    `
    cursor: pointer;
    :hover {
      height: 200px;
      width: 182px;
      background-color: white;
      border-color: transparent;
      box-shadow: 0px 2px 4px -2px rgba(0, 0, 0, 0.1), 0px 4px 6px -1px rgba(0, 0, 0, 0.1);
      .body {
        height: 160px;
        border-top-width: 1px;
      }
      .info-block {
        display: block;
      }
    }
  `}
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 12px;
`;

const Icon = styled.img`
  width: 20px;
  height: 20px;
`;

const MenuContainer = styled.div`
  box-sizing: border-box;
  overflow-y: auto;
  border-top: 1px dashed rgba(0, 0, 0, 0.15);
  transition: height 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const MenuItem = styled.div`
  padding: 6px 12px 0;
  box-sizing: border-box;
  color: ${(props) => (props.selected ? 'var(--td-brand-color-7)' : 'var(--td-font-gray-1)')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  width: 100%;
  cursor: pointer;
  :hover {
    background: rgba(25, 77, 255, 0.08);
  }
`;
